// The typed proposal the triage agent emits and the applier consumes (architecture plan §8.10).
//
// "It is a data structure validated against a schema in `packages/contracts`, not free text, and
// it carries no instruction the applier will follow blindly." Everything the applier does is
// decided from these fields and its own configuration; the agent's gate report, confidence, and
// duplicate decision are recorded and can route a proposal *down* to a human, never authorize a
// write on their own (§13.2, standing build rule 10).

import { z } from "zod";
import { EpicParentPlaneSchema } from "./board-config";
import { IdentifierKindSchema } from "./categories";
import { AccessPlaneSchema } from "./enums";
import { PublicIdSchema } from "./public-id";

/** §8.7's shape decision, plus the two non-drafting outcomes. */
export const PROPOSAL_SHAPES = [
  "child_task",
  "new_story",
  "reuse",
  "not_executable",
  "human_triage",
] as const;
export const ProposalShapeSchema = z.enum(PROPOSAL_SHAPES);
export type ProposalShape = z.infer<typeof ProposalShapeSchema>;

/** The stop conditions listed in §8.12, in the order they are listed there. */
export const STOP_REASONS = [
  "unknown_identifier",
  "destructive_or_irreversible",
  "permission_or_admin_work",
  "credential_or_regulated_data",
  "ambiguous_duplicate",
  "no_wiki_coverage",
  "priority_one",
  "board_on_hold",
  "gate_failed",
  /** Draft-off mode (§8.12): the model was unavailable or over its ceiling; no draft was made. */
  "model_unavailable",
] as const;
export const StopReasonSchema = z.enum(STOP_REASONS);
export type StopReason = z.infer<typeof StopReasonSchema>;

/** An exact identifier the agent extracted (§8.2); part of the canonical incident key (§9.8). */
export const ProposalIdentifierSchema = z.object({
  kind: IdentifierKindSchema,
  value: z.string().min(1),
});

export const DuplicateCandidateSchema = z.object({
  workItemId: z.number().int().positive(),
  score: z.number().min(0).max(1),
  title: z.string().optional(),
});
export type DuplicateCandidate = z.infer<typeof DuplicateCandidateSchema>;

/** The agent's duplicate search result (§8.4). `ambiguous` is a stop, never a merge. */
export const DuplicateDecisionSchema = z.object({
  decision: z.enum(["none", "reuse", "ambiguous"]),
  candidates: z.array(DuplicateCandidateSchema).default([]),
});

export const WikiCitationSchema = z.object({
  path: z.string().min(1),
  url: z.url(),
  title: z.string().optional(),
});
export type WikiCitation = z.infer<typeof WikiCitationSchema>;

/** The board state the agent investigated against (§8.3), so the applier can see what it saw. */
export const BoardSnapshotSchema = z.object({
  takenAt: z.iso.datetime(),
  items: z
    .array(z.object({ id: z.number().int().positive(), rev: z.number().int().positive() }))
    .default([]),
});

export const ProposalGateCheckSchema = z.object({
  id: z.string().min(1),
  passed: z.boolean(),
  /** Present on judged checks (§8.9); absent on mechanical ones. */
  confidence: z.number().min(0).max(1).optional(),
  detail: z.string().optional(),
});

/** The agent's own gate report. Recorded; the applier re-runs the mechanical checks regardless. */
export const ProposalGateSchema = z.object({
  passed: z.boolean(),
  checks: z.array(ProposalGateCheckSchema),
});

export const ProposedTaskSchema = z.object({
  title: z.string().min(1),
  /** Markdown to the Task Description template. */
  description: z.string().min(1),
  tags: z.array(z.string().min(1)).default([]),
});

export const ProposedStorySchema = z.object({
  title: z.string().min(1),
  /** Markdown to the User Story Description template. */
  description: z.string().min(1),
  /** Markdown checkboxes, unchecked. */
  acceptanceCriteria: z.string().min(1),
  tags: z.array(z.string().min(1)).default([]),
  priority: z.number().int().min(1).max(4).optional(),
  childTasks: z.array(ProposedTaskSchema).default([]),
});

export const ProposalStopSchema = z.object({
  reason: StopReasonSchema,
  detail: z.string().min(1),
});

const ProposalBase = z.object({
  schemaVersion: z.literal(1),
  requestId: z.uuid(),
  publicId: PublicIdSchema,
  /** The job event that produced this run; the result's dedupe id derives from it (§8.11). */
  jobEventId: z.uuid(),
  /** The pinned release tag of prompt, model, tools, corpus, and validators (§13.2). */
  behaviourSurfaceVersion: z.string().min(1),
  category: z.string().min(1),
  accessPlane: AccessPlaneSchema,
  /** The Epic rollup the story belongs under (§8.5). Required when a story is created. */
  epicPlane: EpicParentPlaneSchema.optional(),
  identifiers: z.array(ProposalIdentifierSchema).default([]),
  boardSnapshot: BoardSnapshotSchema,
  duplicateDecision: DuplicateDecisionSchema,
  citations: z.array(WikiCitationSchema).default([]),
  gate: ProposalGateSchema,
  /** Overall self-assessed confidence. Routes down, never authorizes (§13.2). */
  confidence: z.number().min(0).max(1),
  /**
   * §8.4, §8.12: the category or the decision needs a person before any write — a merge in an
   * access, identity, HR, or device category, or a category that always escalates. The applier
   * queues such a proposal whatever the autonomy level; a reviewer's promotion is the authority.
   */
  humanReviewRequired: z.boolean().default(false),
  humanReviewReason: z.string().min(1).optional(),
  /**
   * Model output that may reach the requester only at L2 or above, and only after a write, or
   * when a reviewer releases it (§8.11). Held otherwise.
   */
  requesterSafeSummary: z.string().min(1),
});

export const ProposalSchema = z
  .discriminatedUnion("shape", [
    ProposalBase.extend({ shape: z.literal("new_story"), story: ProposedStorySchema }),
    ProposalBase.extend({
      shape: z.literal("child_task"),
      task: ProposedTaskSchema,
      /** The existing story to put the Task under. Must be the primary type, never an Epic. */
      parentWorkItemId: z.number().int().positive(),
    }),
    ProposalBase.extend({
      shape: z.literal("reuse"),
      reuseWorkItemId: z.number().int().positive(),
      /** A progress-comment-shaped note for the reused card; the applier adds the marker. */
      reuseNote: z.string().min(1).optional(),
    }),
    ProposalBase.extend({ shape: z.literal("not_executable"), stop: ProposalStopSchema }),
    ProposalBase.extend({ shape: z.literal("human_triage"), stop: ProposalStopSchema }),
  ])
  .superRefine((proposal, ctx) => {
    const drafting = proposal.shape === "new_story" || proposal.shape === "child_task";
    if (drafting && proposal.accessPlane === "unknown") {
      // §8.5: unknown is a reason to stop, never a default to draft against.
      ctx.addIssue({
        code: "custom",
        message: "a drafting proposal cannot carry an unknown access plane; stop instead (§8.5)",
        path: ["accessPlane"],
      });
    }
    if (proposal.shape === "new_story" && !proposal.epicPlane) {
      ctx.addIssue({
        code: "custom",
        message: "a new story needs an epicPlane so the applier can pick its Epic (§8.5)",
        path: ["epicPlane"],
      });
    }
    if (proposal.shape === "reuse" && proposal.duplicateDecision.decision === "ambiguous") {
      ctx.addIssue({
        code: "custom",
        message: "an ambiguous duplicate decision cannot propose a reuse (§8.4)",
        path: ["duplicateDecision", "decision"],
      });
    }
  });

export type Proposal = z.infer<typeof ProposalSchema>;
export type ProposalInput = z.input<typeof ProposalSchema>;
export type ProposedStory = z.infer<typeof ProposedStorySchema>;
export type ProposedTask = z.infer<typeof ProposedTaskSchema>;
